import { useEffect, useRef } from 'react';

import { useEditorRemixLifecycle } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';

import { projectEditorRemixLifecycleToThinking } from './utils/editor-remix-lifecycle-projection';
import type { RemixThinkingLifecycleProjection } from './utils/editor-remix-lifecycle-projection';

type LastMirrored = {
	isComplete: boolean;
	isThinking: boolean;
	remixId: string | undefined;
};

/**
 * Dispatches the projected `isThinking` / `isComplete` pair whenever `(remixId, isThinking,
 * isComplete)` changes. `remixId` is part of the key so a new session, or a session's release,
 * writes again even when the projected booleans repeat. Writes nothing while there is no session
 * to describe: the initial ProseMirror state is already `false` / `false`.
 */
export const useEditorRemixLifecycleMirror = ({
	animationFinished,
	presentationRemixId,
	dispatchProjection,
}: {
	animationFinished: boolean;
	dispatchProjection: (projection: RemixThinkingLifecycleProjection) => void;
	presentationRemixId: string | null;
}): void => {
	const { remixId, phase, terminalPhase } = useEditorRemixLifecycle();
	const effectivePhase = terminalPhase ?? phase;

	const lastRef = useRef<LastMirrored | undefined>(undefined);

	useEffect(() => {
		const last = lastRef.current;

		if (remixId === undefined) {
			// Nothing to release: either the initial mount, or the release write already happened.
			if (last?.remixId === undefined) {
				return;
			}

			const projection = projectEditorRemixLifecycleToThinking({
				phase: 'inactive',
				animationFinished: false,
			});
			dispatchProjection(projection);
			lastRef.current = { remixId: undefined, ...projection };
			return;
		}

		// The React lifecycle context and the synchronous registry can briefly be on adjacent
		// sessions during replacement. Never combine A's live phase with B's presentation. A
		// terminal phase is different: it is latched before ownership release, so its inactive
		// projection remains authoritative after the synchronous registry has cleared its identity.
		const hasLatchedTerminalOutcome = terminalPhase === 'error' || terminalPhase === 'aborted';
		if (presentationRemixId !== remixId && !hasLatchedTerminalOutcome) {
			return;
		}

		const projection = projectEditorRemixLifecycleToThinking({
			phase: effectivePhase,
			animationFinished,
		});

		if (
			last?.remixId === remixId &&
			last.isThinking === projection.isThinking &&
			last.isComplete === projection.isComplete
		) {
			return;
		}

		// Recorded after the dispatch returns so a throwing dispatcher stays retryable.
		dispatchProjection(projection);
		lastRef.current = { remixId, ...projection };
	}, [
		animationFinished,
		dispatchProjection,
		remixId,
		effectivePhase,
		presentationRemixId,
		terminalPhase,
	]);
};
